<template>
	<v-app-bar class="top-bar" order="0" height="64" elevation="2">
		<template #prepend>
			<v-img class="logo" width="38" height="38" eager />
		</template>
		<v-app-bar-title>{{ appName }}</v-app-bar-title>
		<StickyNotification />

		<template #append>
			<v-tooltip location="bottom" text="Notifications">
				<template #activator="{props}">
					<div v-bind="props">
						<NotificationsContainer />
					</div>
				</template>
			</v-tooltip>

			<v-menu close-on-content-click>
				<template #activator="{props}">
					<v-btn v-bind="props" color="primary" class="text-h3 px-0">
						<template #prepend>
							<v-icon icon="mdi-view-grid" />
						</template>
						Modules
					</v-btn>
				</template>

				<template #default>
					<v-list :items="availablePanels" @click:select="ev => openPanel(ev.id)" />
				</template>
			</v-menu>

			<v-tooltip
				v-if="liveMode"
				location="bottom"
				:text="userPeer ? 'Release before switching studios.' : 'Studio selection'"
			>
				<template #activator="{props}">
					<div v-bind="props">
						<v-btn
							class="text-h3"
							variant="outlined"
							:color="studioSettings ? 'primary' : 'secondary'"
							:disabled="!!userPeer"
							@click="openSelectStudioDialog()"
						>
							<template #append>
								<v-icon size="30"> mdi-microphone-variant</v-icon>
							</template>
							{{ studioSettings ? `Studio ${studioSettings.reference}` : "Select Studio" }}
						</v-btn>
					</div>
				</template>
			</v-tooltip>

			<SnackbarNotification />

			<v-tooltip location="bottom" text="Job logs">
				<template #activator="{props}">
					<div v-bind="props">
						<JobsContainer />
					</div>
				</template>
			</v-tooltip>

			<v-tooltip location="bottom" text="Midi UI">
				<template #activator="{props}">
					<v-btn
						v-if="studioSettings && userIsHost"
						v-bind="props"
						elevation="0"
						class="pa-0"
						size="30"
						@click="openMidiPanel"
					>
						<v-icon size="30" :color="isMidiOpen ? 'red' : midiConnected ? 'blue' : 'white'">
							mdi-tune-vertical
						</v-icon>
					</v-btn>
				</template>
			</v-tooltip>

			<v-tooltip location="bottom" text="Help">
				<template #activator="{props}">
					<a v-bind="props" style="text-decoration: none" :href="supportUrl" target="_blank">
						<v-icon size="30">mdi-help-circle</v-icon>
					</a>
				</template>
			</v-tooltip>

			<v-menu v-model="menuOpen">
				<template #activator="{props}">
					<v-avatar v-bind="props" size="40">
						<v-icon>mdi-account</v-icon>
					</v-avatar>
				</template>
				<v-list>
					<v-list-item disabled height="60">
						<template #default>
							<div class="d-inline-flex">
								<v-icon size="24" class="mr-2">mdi-account</v-icon>
								{{ userFirstLastName }}
							</div>
						</template>
					</v-list-item>
					<v-list-item v-if="environment === 'ZETTACLOUD'" @click="openOrganizationsManager">
						<v-list-item-title class="text-body-2"> View Organizations </v-list-item-title>
					</v-list-item>
					<v-list-item :disabled="!studioSettings && !newStudio" @click="openStudioSettings">
						<v-list-item-title class="text-body-2"> View Studio Settings </v-list-item-title>
					</v-list-item>
					<v-list-item
						v-if="environment === 'ZETTACLOUD' && hasUserPreferencesFeature"
						@click="openUserPreferences"
					>
						<v-list-item-title class="text-body-2"> Preferences </v-list-item-title>
					</v-list-item>
					<v-list-item
						v-if="environment === 'ZETTACLOUD' && hasUserPreferencesFeature"
						@click="openUserPreferences"
					>
						<v-list-item-title class="text-body-2"> Preferences </v-list-item-title>
					</v-list-item>
					<v-list-item @click.stop>
						<v-list-item-title>
							<v-switch
								:model-value="liveMode"
								density="compact"
								color="primary"
								hide-details
								:disabled="userAnyRole"
								@update:model-value="setLiveMode"
							>
								<template #prepend>
									<div class="text-body-2">Live mode</div>
								</template>
							</v-switch>
						</v-list-item-title>
					</v-list-item>
					<v-list-item @click.stop>
						<v-list-item-title>
							<v-switch
								:model-value="darkMode"
								density="compact"
								color="primary"
								hide-details
								@update:model-value="setDarkMode"
							>
								<template #prepend>
									<div class="text-body-2">Dark mode</div>
								</template>
							</v-switch>
						</v-list-item-title>
					</v-list-item>
					<v-list-item>
						<v-list-item-title class="text-center">
							<v-label>Version: {{ version }}</v-label>
						</v-list-item-title>
					</v-list-item>
					<v-divider></v-divider>
					<v-menu v-if="userIsHost" :close-on-content-click="true">
						<template #activator="{props}">
							<v-list-item v-bind="props" height="60">
								<v-btn variant="outlined" block color="secondary"> Logout </v-btn>
							</v-list-item>
						</template>
						<GeneralConfirmation
							message="You are currently controlling the station as host."
							:button-options="buttonOptions.generalConfirmation"
							@agree="signOut"
						/>
					</v-menu>
					<v-list-item v-else height="60">
						<v-btn variant="outlined" block color="secondary" @click="signOut"> Logout </v-btn>
					</v-list-item>
				</v-list>
			</v-menu>

			<UserPreferencesDialog
				v-if="environment === 'ZETTACLOUD' && hasUserPreferencesFeature"
				v-model="openUserPreferencesDialogIsOpen"
			/>

			<OrganizationsManager
				v-if="organizationsManagerDialogIsOpen"
				v-model:dialog="organizationsManagerDialogIsOpen"
				@close="organizationsManagerDialogIsOpen = false"
			/>
			<StudioSettingsDialog v-model:dialog="studioSettingsDialog" @close="studioSettingsDialog.open = false" />
		</template>
	</v-app-bar>
	<SelectStudioDialog
		v-if="studioSelectDialog.open"
		:dialog="studioSelectDialog"
		:options="studioSelectDialog.options"
		@close="studioSelectDialog.open = false"
	/>
</template>

<script>
/* Modules */
import GeneralConfirmation from "@/components/dialogs/GeneralConfirmation.vue";
import JobsContainer from "@/components/JobsContainer.vue";
import NotificationsContainer from "@/components/NotificationsContainer.vue";
import SelectStudioDialog from "@/components/dialogs/SelectStudioDialog.vue";
import SnackbarNotification from "@/components/SnackbarNotification.vue";
import StickyNotification from "@/components/StickyNotification.vue";
import StudioSettingsDialog from "@/components/dialogs/StudioSettingsDialog.vue";
import OrganizationsManager from "@/components/OrganizationsManager.vue";

/* Mixins */
import SequencerPlayerStudioStatus from "@/mixins/SequencerPlayerStudioStatus";

/*Enums*/
import ConferenceRoles from "@/enums/ConferenceRoles";
import Panels from "@/enums/Panels";

/* Libraries */
import emitter from "@/emitter";
import config from "@/config";
import UserPreferencesDialog from "@/components/dialogs/UserPreferencesDialog.vue";

export default {
	name: "TopBar",

	components: {
		UserPreferencesDialog,
		GeneralConfirmation,
		StickyNotification,
		NotificationsContainer,
		StudioSettingsDialog,
		SnackbarNotification,
		JobsContainer,
		OrganizationsManager,
		SelectStudioDialog
	},

	mixins: [SequencerPlayerStudioStatus],

	data() {
		return {
			menuOpen: false,
			studioSettingsDialog: {
				open: false,
				mode: null
			},
			newStudio: false,
			midiConnected: false,
			buttonOptions: {
				generalConfirmation: {
					agreeBtn: {
						enabled: true,
						message: "Release and Signout"
					},
					cancelBtn: {
						enabled: true,
						message: "Cancel"
					}
				}
			},
			studioSelectDialog: {
				open: false,
				options: {}
			},
			organizationsManagerDialogIsOpen: false,
			openUserPreferencesDialogIsOpen: false
		};
	},

	computed: {
		availablePanels() {
			return Panels.getArray({
				liveMode: this.liveMode,
				userIsHost: this.userIsHost,
				stationID: this.liveMode ? this.$store.getters.liveModeSelectedStationID() : null
			});
		},

		appName() {
			return this.$store.getters.getAccount()?.display_name || this.$store.getters.getAccount()?.company;
		},

		environment() {
			return config.ORIGIN;
		},

		userFirstLastName() {
			return this.$store.getters.getUserFullName();
		},

		liveMode() {
			return this.$store.getters.liveMode();
		},

		darkMode() {
			return this.$store.getters.darkMode();
		},

		version() {
			return config.APP_VERSION;
		},

		supportUrl() {
			return `${config.SUPPORT_URL}${config.ORIGIN === "SOUNDPLUS" ? "/SitePages/Live-UI.aspx" : ""}`;
		},

		hasUserPreferencesFeature() {
			return this.$store.getters.isFeatureEnabled({
				object_id: this.$store.getters.getOrgID(),
				reference: "zettacloud_live_console_user_preferences"
			});
		},

		isMidiOpen() {
			return this.$store.getters.getIsMidiOpen();
		}
	},

	watch: {
		darkMode(enabled) {
			this.$vuetify.theme.change(enabled ? "dark" : "light");
		}
	},

	mounted() {
		emitter.on("add-new-studio-settings", this.openNewStudioDialog);
		emitter.on("midiWindowClosed", this.onMidiPanelClosed);
		emitter.on("midiWindowOpen", this.openMidiPanel);

		if (navigator.requestMIDIAccess) {
			navigator.requestMIDIAccess({sysex: true}).then(
				access => {
					this.midiConnected = access.inputs.size > 0 && access.outputs.size > 0;
					access.onstatechange = event => {
						this.midiConnected = event.port.state === "connected";
					};
				},
				() => {
					this.midiConnected = false;
				}
			);
		}

		this.$store.dispatch("getUserFeatures", {object_id: this.$store.getters.getOrgID()});
	},

	beforeUnmount() {
		emitter.off("add-new-studio-settings", this.openNewStudioDialog);
		emitter.off("midiWindowClosed", this.onMidiPanelClosed);
		emitter.off("midiWindowOpen", this.openMidiPanel);
	},

	methods: {
		openOrganizationsManager() {
			this.organizationsManagerDialogIsOpen = true;
		},

		openUserPreferences() {
			this.openUserPreferencesDialogIsOpen = true;
		},

		openStudioSettings() {
			this.studioSettingsDialog = {
				open: true,
				mode: !this.studioSettings && this.newStudio ? "add" : "edit"
			};
		},

		setLiveMode(enabled) {
			this.$store.commit("setLiveMode", enabled);
			this.$store.dispatch("resetState");
			this.menuOpen = false;
		},

		setDarkMode(enabled) {
			let studioSettings = this.$store.getters.getStudioSettings();
			if (studioSettings) {
				studioSettings.preferences = {...studioSettings.preferences, darkMode: enabled};
				this.$store.dispatch("editStudioSettings", studioSettings);
			} else {
				this.$store.commit("setDarkMode", enabled);
			}
		},

		async signOut() {
			await this.studioDisconnect();
			this.$store.dispatch("signOut");
		},

		studioDisconnect() {
			if (this.sequencerIsLive && !this.sequencerWaitingForEndOfShow && this.userIsHost) {
				return this.$store
					.dispatch("deleteRecordingSession", this.startLiveSequencerSnapshot.AssetID)
					.then(() => {
						// return new Promise to ensure .then() chain execution before dispatch of signOut
						// sequencerEndLiveRequest and disconnectStudio are synchronous functions
						return new Promise(resolve => {
							this.$store.dispatch("sequencerEndLiveRequest");
							this.$store.dispatch("disconnectStudio", ConferenceRoles.Host);
							resolve();
						});
					});
			}
		},

		openSelectStudioDialog() {
			this.studioSelectDialog = {
				open: true,
				options: {
					studioSettings: this.studioSettings,
					parent: this
				}
			};
		},

		openPanel(panel, popout = false) {
			if (panel === "midi") {
				this.$store.commit("setIsMidiOpen", true);
			}
			emitter.emit("docking:openPanel", {name: panel, floating: !popout, popout});
		},

		openMidiPanel() {
			if (this.isMidiOpen) {
				return;
			}
			this.openPanel("midi", true);
		},

		onMidiPanelClosed() {
			this.$store.commit("setIsMidiOpen", false);
		},

		openNewStudioDialog() {
			this.newStudio = true;
			this.openStudioSettings();
		}
	}
};
</script>

<style lang="scss" scoped>
.top-bar :deep(.v-toolbar__append > *) {
	margin: 0 12px;
}

:deep(.v-selection-control) {
	.v-switch__track {
		height: 12px;
	}

	.v-switch__thumb {
		height: 18px;
		width: 18px;
	}
}
</style>